import { SxProps } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useCompanyController } from 'api/controllers/CompanyController';

import { Company } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { useCurrency } from 'hooks/useCurrency';

import { getDashboardRevenueColumns } from 'utils/helpers/revenueHelper';

import { DashboardGrid } from './DashboardGrid';

interface RevenueGridProps {
  isOnAllCompanies: boolean;
  sx?: SxProps;
  noDataMessage?: string;
  gridTitle: string;
  callToApi: (companyId?: number) => Promise<RevenueInfo[]>;
}

type RevenueInfo = {
  id: number;
  amount: number;
  company: string;
  counterparty: string;
  date: string;
  documentNumber: string;
};

export const RevenueDashboardGrid = ({
  sx,
  isOnAllCompanies,
  noDataMessage,
  gridTitle,
  callToApi
}: RevenueGridProps) => {
  const { companyId } = useParams();
  const location = useLocation();
  const { translate } = useTranslations();
  const { getCurrentCompanyCurrency } = useCurrency();

  const { getAllCompanies } = useCompanyController();

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [revenueInfo, setRevenueInfo] = useState<RevenueInfo[]>();
  const [companies, setCompanies] = useState<Company[]>();

  const getCompanies = useCallback(async () => {
    const result = await getAllCompanies();
    setCompanies(result);
  }, [getAllCompanies]);

  const getRevenue = useCallback(async () => {
    try {
      const response = await callToApi(
        isOnAllCompanies ? undefined : Number(companyId)
      );

      if (response) {
        setRevenueInfo(response);
      } else {
        setRevenueInfo([]);
      }
    } catch (error) {
      setRevenueInfo([]);
    }
  }, [companyId, callToApi]);

  const columns = useMemo(
    () =>
      getDashboardRevenueColumns(
        isOnAllCompanies,
        translate,
        isOnAllCompanies ? undefined : getCurrentCompanyCurrency()
      ),
    [translate, isOnAllCompanies, getCurrentCompanyCurrency]
  );

  useEffect(() => {
    getCompanies();
  }, [getCompanies]);

  useEffect(() => {
    setRevenueInfo(undefined);
    if (!companies) return;
    getRevenue();
  }, [companies, location, callToApi]);

  return (
    <DashboardGrid
      title={gridTitle}
      data={revenueInfo}
      columns={columns}
      getRowId={(row) => row.id || 0}
      sx={sx}
      selectedRows={selectedRows}
      onRowSelectionChange={setSelectedRows}
      noDataMessage={noDataMessage}
    />
  );
};
